import React, { useState, useEffect, Suspense, lazy, useTransition, useCallback } from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';
import Home from './pages/Home';
import RouteProgressBar from './components/RouteProgressBar';
import { Page } from './types';

// PERFORMANCE: Route-based Code Splitting
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const PartnerPage = lazy(() => import('./pages/PartnerPage'));
const DownloadPage = lazy(() => import('./pages/DownloadPage'));
const UnsubscribePage = lazy(() => import('./pages/Unsubscribe'));
const DeleteAccPage = lazy(() => import('./pages/DeleteAccPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const AppAgbPage = lazy(() => import('./pages/AppAgbPage'));
const ImpressumPage = lazy(() => import('./pages/ImpressumPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostDetailPage = lazy(() => import('./pages/BlogPostDetailPage'));
const SupportersPage = lazy(() => import('./pages/SupportersPage'));
const KnowledgeHubPage = lazy(() => import('./pages/KnowledgeHubPage'));
const KnowledgePostDetailPage = lazy(() => import('./pages/KnowledgePostDetailPage'));
//const ChangelogPage = lazy(() => import('./pages/ChangelogPage'));
const EducationBusinessPage = lazy(() => import('./pages/EducationBusinessPage'));
const WaitlistConfirmPage = lazy(() => import('./pages/WaitlistConfirmPage'));
const RegisterConfirmPage = lazy(() => import('./pages/RegisterConfirmPage'));
const MedicalTransparencyPage = lazy(() => import('./pages/MedicalTransparencyPage'));
//const CollaboratePage = lazy(() => import('./pages/CollaboratePage'));
const StatusPage = lazy(() => import('./pages/StatusPage'));
const BugTrackerPage = lazy(() => import('./pages/BugTrackerPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));

const App: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<Page>('start');
  const [currentPostId, setCurrentPostId] = useState<number | null>(null);
  const [currentKnowledgeId, setCurrentKnowledgeId] = useState<number | null>(null);
  const [highContrast, setHighContrast] = useState(false);
  const [fontSize, setFontSize] = useState<number>(100);
  
  const [isPending, startTransition] = useTransition();

  const updateMetaTags = useCallback(() => {
    let title = "ErsteHilfe2Go – Erste Hilfe per App lernen";
    let description = "ErsteHilfe2Go ist die Erste Hilfe App für Deutschland. Klare Notfall-Anleitungen, offline nutzbar, verantwortungsvoll und verständlich.";
    
    switch (currentPage) {
      case 'ueber-uns':
        title = "Uber uns - Erste Hilfe 2 Go";
        description = "Erfahre mehr über ErsteHilfe2Go: Mission, Team, Leistungen und Standort der Erste Hilfe App für Deutschland und den DACH-Raum.";
        break;
      case 'funktionen': 
        title = "Funktionen der ErsteHilfe2Go App";
        description = "Entdecke die Funktionen der ErsteHilfe2Go App: klare Erste Hilfe Anleitungen, offline nutzbar, verständlich und stressoptimiert.";
        break;
      case 'faq': 
        title = "FAQ – ErsteHilfe2Go";
        description = "Häufige Fragen zur ErsteHilfe2Go Erste Hilfe App – verständlich beantwortet für mehr Sicherheit im Notfall.";
        break;
      case 'blog': 
        title = "Blog – Erste Hilfe & Notfallwissen";
        description = "Im Blog von ErsteHilfe2Go findest du aktuelle Beiträge rund um Erste Hilfe, Notfallwissen und Prävention.";
        break;
      case 'wissenshub': 
        title = "Wissenshub – Erste Hilfe Wissen";
        description = "Der Wissenshub von ErsteHilfe2Go bietet verständliche Informationen rund um Erste Hilfe und Notfälle.";
        break;
      case 'status': 
        title = "Entwicklungsstatus von ErsteHilfe2Go";
        description = "Informiere dich über den aktuellen Entwicklungsstand der ErsteHilfe2Go Erste Hilfe App.";
        break;
      case 'mitarbeiten': 
        title = "Bei ErsteHilfe2Go mitarbeiten";
        description = "Unterstütze ErsteHilfe2Go und hilf mit, Erste Hilfe Wissen für alle zugänglich zu machen.";
        break;
      case 'warteliste': 
        title = "Warteliste – ErsteHilfe2Go";
        description = "Trage dich in die Warteliste von ErsteHilfe2Go ein und bleibe über den Start der Erste Hilfe App informiert.";
        break;
      case 'medizinische-transparenz':
        title = "Medizinische Transparenz bei ErsteHilfe2Go";
        description = "Erfahre, wie die Inhalte von ErsteHilfe2Go entstehen und welche Grundsätze der medizinischen Transparenz gelten.";
        break;
      case 'warteliste-bestaetigung':
        title = "Anmeldung bestätigt – ErsteHilfe2Go";
        description = "Vielen Dank für deine Anmeldung zur Warteliste von ErsteHilfe2Go!";
        break;
      case 'registrierung-bestaetigung':
        title = "Registrierung bestätigt - ErsteHilfe2Go";
        description = ""
        break;
      case 'passwort-zuruecksetzen':
        title = "Passwort zurücksetzen - ErsteHilfe2Go";
        description = "Setze dein Passwort für ErsteHilfe2Go zurück und erhalte wieder Zugang zu deinem Account.";
        break;
    }

    document.title = title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', description);
  }, [currentPage]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    updateMetaTags();
  }, [updateMetaTags]);

  const navigate = (page: Page, id?: number) => {
    startTransition(() => {
      setCurrentPage(page);
      if (page === 'blog-beitrag' && id !== undefined) {
        setCurrentPostId(id);
        setCurrentKnowledgeId(null);
        const path = `/blog/${id}`;
        window.history.pushState(null, '', path);
      } else if (page === 'wissens-artikel' && id !== undefined) {
        setCurrentKnowledgeId(id);
        setCurrentPostId(null);
        const path = `/wissen/${id}`;
        window.history.pushState(null, '', path);
      } else {
        setCurrentPostId(null);
        setCurrentKnowledgeId(null);
        const path = page === 'start' ? '/' : `/${page}`;
        window.history.pushState(null, '', path);
      }
    });
  };

  // Convert legacy hash routes to history paths and handle navigation via popstate
  useEffect(() => {
    const validPages: Page[] = [
      'start', 'ueber-uns', 'funktionen', 'faq', 'partner', 'warteliste', 
      'datenschutz', 'impressum', 'blog', 'wissenshub', 
      'status', 'mitarbeiten', 'medizinische-transparenz', 
      'warteliste-bestaetigung', 'registrierung-bestaetigung', 'abmelden', 'konto-loeschen',
      'bildung-business', 'changelog', 'unterstuetzer', 'error', 'passwort-zuruecksetzen',
      'app-agb', 'bugs',
    ];

    const hashToPath = (fullHash: string) => {
      // Preserve query string when converting legacy hashes like
      // #passwort-zuruecksetzen?token=xyz -> /passwort-zuruecksetzen?token=xyz
      const h = fullHash.replace(/^#\/?/, '').replace(/\/$/, '');
      const parts = h.split('?');
      const pathPart = parts[0] || '';
      const queryPart = parts[1] ? `?${parts.slice(1).join('?')}` : '';

      if (pathPart.startsWith('blog-artikel/')) {
        const id = parseInt(pathPart.split('/')[1]);
        if (!isNaN(id)) return `/blog/${id}${queryPart}`;
      }
      if (pathPart.startsWith('wissen/')) {
        const id = parseInt(pathPart.split('/')[1]);
        if (!isNaN(id)) return `/wissen/${id}${queryPart}`;
      }
      const base = pathPart === '' ? '/' : `/${pathPart}`;
      return `${base}${queryPart}`;
    };

    const pathToState = (path: string) => {
      const p = path.replace(/\/$/, '');
      if (p === '' || p === '/') return { page: 'start' as Page };
      const parts = p.replace(/^\//, '').split('/');
      if (parts[0] === 'blog' && parts[1]) return { page: 'blog-beitrag' as Page, id: parseInt(parts[1]) };
      if (parts[0] === 'wissen' && parts[1]) return { page: 'wissens-artikel' as Page, id: parseInt(parts[1]) };
      const maybe = parts[0] as Page;
      if (validPages.includes(maybe)) return { page: maybe };
      return { page: 'start' as Page };
    };

    const handlePathChange = () => {
      startTransition(() => {
        const loc = window.location.pathname;
        const state = pathToState(loc);
        setCurrentPostId(null);
        setCurrentKnowledgeId(null);
        if (state.page === 'blog-beitrag' && state.id && !isNaN(state.id)) {
          setCurrentPage('blog-beitrag');
          setCurrentPostId(state.id);
        } else if (state.page === 'wissens-artikel' && state.id && !isNaN(state.id)) {
          setCurrentPage('wissens-artikel');
          setCurrentKnowledgeId(state.id);
        } else {
          setCurrentPage(state.page);
        }
      });
    };

    // If there's a legacy hash on first load, convert it to a path
    if (window.location.hash) {
      const path = hashToPath(window.location.hash);
      if (path !== window.location.pathname) {
        window.history.replaceState(null, '', path);
      }
    }

    // Handle browser navigation
    window.addEventListener('popstate', handlePathChange);
    // Initialize state from current path
    handlePathChange();
    return () => window.removeEventListener('popstate', handlePathChange);
  }, []);

  const renderPage = () => {
    switch (currentPage) {
      case 'ueber-uns': return <AboutPage />;
      case 'funktionen': return <FeaturesPage navigate={navigate} />;
      case 'faq': return <FAQPage />;
      case 'partner': return <PartnerPage />;
      case 'warteliste': return <DownloadPage navigate={navigate} />;
      case 'datenschutz': return <PrivacyPage />;
      case 'app-agb': return <AppAgbPage />;
      case 'impressum': return <ImpressumPage />;
      case 'blog': return <BlogPage navigate={navigate} />;
      case 'blog-beitrag': return <BlogPostDetailPage postId={currentPostId} navigate={navigate} />;
      case 'wissenshub': return <KnowledgeHubPage navigate={navigate} />;
      case 'wissens-artikel': return <KnowledgePostDetailPage articleId={currentKnowledgeId} navigate={navigate} />;
      //case 'changelog': return <ChangelogPage />;
      case 'bildung-business': return <EducationBusinessPage />;
      case 'warteliste-bestaetigung': return <WaitlistConfirmPage navigate={navigate} />;
      case 'registrierung-bestaetigung': return <RegisterConfirmPage navigate={navigate} />;
      case 'medizinische-transparenz': return <MedicalTransparencyPage />;
      //case 'mitarbeiten': return <CollaboratePage />;
      case 'unterstuetzer': return <SupportersPage />;
      case 'abmelden': return <UnsubscribePage />;
      case 'konto-loeschen': return <DeleteAccPage />;
      case 'status': return <StatusPage navigate={navigate} />;
      //case 'bugs': return <BugTrackerPage navigate={navigate} />;
      case 'passwort-zuruecksetzen': return <ResetPasswordPage />;
      default: return <Home navigate={navigate} />;
    }
  };

  return (
    <div 
      className={`min-h-screen flex flex-col overflow-x-hidden transition-all duration-300 ${
        highContrast ? 'bg-black text-white' : 'bg-white text-gray-900'
      }`}
      style={{ fontSize: `${fontSize}%` }}
    >
      <RouteProgressBar isPending={isPending} />

      <Header 
        currentPage={currentPage} 
        navigate={navigate} 
        highContrast={highContrast}
        setHighContrast={setHighContrast}
        fontSize={fontSize}
        setFontSize={setFontSize}
      />
      
      <main className={`flex-grow transition-opacity duration-300 ${isPending ? 'opacity-70' : 'opacity-100'}`}>
        <Suspense fallback={null}>
          {renderPage()}
        </Suspense>
      </main>
      
      <Footer navigate={navigate} />
      <CookieBanner navigate={navigate} />
    </div>
  );
};

export default App;
